import React from 'react';
import { Helmet } from 'react-helmet';
import { Phone, MessageCircle, Mail, ShieldCheck, Wrench, LifeBuoy, Package, BookOpen, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';

const WA = 'https://wa.me/917011370322';

const CHANNELS = [
  { icon: Phone, title: 'Call Support', desc: 'Talk to our support team directly', action: '+91-7011370322', href: 'tel:+917011370322', testid: 'support-call' },
  { icon: MessageCircle, title: 'WhatsApp Support', desc: 'Fastest response for service, parts & manuals', action: 'Chat on WhatsApp', href: `${WA}?text=${encodeURIComponent('Hello Harpalyk Support, I need help with my robot.')}`, external: true, testid: 'support-whatsapp' },
  { icon: Mail, title: 'Email Support', desc: 'Share your issue, order ID and photos', action: 'support@harpalyk.com', href: 'mailto:support@harpalyk.com', testid: 'support-email' },
];

const RESOURCES = [
  { icon: ShieldCheck, title: 'Warranty Policy', desc: '1 or 2-year warranty on robots — coverage details and claims', href: '/warranty-policy' },
  { icon: Wrench, title: 'Installation & Delivery', desc: 'Delivery timelines and free professional installation', href: '/shipping-policy' },
  { icon: LifeBuoy, title: 'Troubleshooting & Care', desc: 'Maintenance tips to keep your robot performing like new', href: '/blog/robot-vacuum-maintenance-tips-long-life' },
  { icon: Package, title: 'Genuine Spare Parts', desc: 'Batteries, filters and consumables for every robot', href: '/accessories' },
  { icon: BookOpen, title: 'Product Manuals', desc: 'Request the manual for your robot model on WhatsApp', href: `${WA}?text=${encodeURIComponent('Hello Harpalyk, please share the product manual for my robot.')}`, external: true },
];

export default function SupportPage() {
  return (
    <>
      <Helmet>
        <title>Contact Support — Service, Warranty & Spare Parts | Harpalyk India</title>
        <meta name="description" content="Get Harpalyk customer support — service requests, warranty claims, spare parts, manuals and troubleshooting for SMARTBOT, SHINEBOT, POOLBOT and LAWNBOT robots. Call, WhatsApp or email our pan-India support team." />
        <link rel="canonical" href="https://harpalyk.com/support" />
      </Helmet>
      <Header />
      <main className="pt-20" data-testid="support-page">
        <div className="h-1 bg-gradient-to-r from-[#0057B8] via-sky-400 to-transparent" aria-hidden="true" />
        <Breadcrumbs items={[{ label: 'Contact Support' }]} />

        <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <p className="font-heading text-sm font-bold tracking-[0.3em] text-white/50 uppercase mb-3">Harpalyk Care</p>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-4">Contact Support</h1>
            <p className="text-lg text-gray-300 max-w-2xl">
              Service requests, warranty claims, spare parts or setup help — our pan-India support team is here for every Harpalyk robot.
            </p>
          </div>
        </section>

        <section className="py-14 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {CHANNELS.map((c) => (
              <a
                key={c.title}
                href={c.href}
                target={c.external ? '_blank' : undefined}
                rel={c.external ? 'noopener noreferrer' : undefined}
                data-testid={c.testid}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow p-7"
              >
                <c.icon className="w-7 h-7 text-[#0057B8]" aria-hidden="true" />
                <h2 className="text-lg font-bold text-gray-900 mt-4">{c.title}</h2>
                <p className="text-sm text-gray-500 mt-1">{c.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 mt-4 group-hover:text-emerald-700">
                  {c.action} <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </section>

        <section className="py-14 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Support Resources</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {RESOURCES.map((r) => (
                <a
                  key={r.title}
                  href={r.href}
                  target={r.external ? '_blank' : undefined}
                  rel={r.external ? 'noopener noreferrer' : undefined}
                  data-testid={`support-resource-${r.title.toLowerCase().replace(/[\s&]+/g, '-')}`}
                  className="group flex items-start gap-4 rounded-2xl border border-gray-100 bg-white p-6 hover:bg-gray-50 transition-colors"
                >
                  <r.icon className="w-6 h-6 text-[#0057B8] mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block font-semibold text-gray-900">{r.title}</span>
                    <span className="block text-sm text-gray-500 mt-1">{r.desc}</span>
                  </span>
                </a>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-10">
              Looking to see a robot in action instead?{' '}
              <a href="/#contact" className="text-emerald-700 font-medium hover:underline" data-testid="support-demo-link">Book a free product demo</a>.
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </>
  );
}
